import { hideElement } from '@/helpers/animations_helpers';

/**
 * Handles smooth removal of elements with Turbo streams
 * Elements with data-smooth-turbo-remove="true" will be animated out before removal
 */
class SmoothTurboRemove {
  constructor() {
    this.pendingRemovals = new Map();
    this.initialize();
  }

  initialize() {
    document.addEventListener(
      'turbo:before-stream-render',
      this.handleTurboStream.bind(this)
    );

    // Clean up any elements still animating out if the page is about to unload
    document.addEventListener(
      'turbo:before-visit',
      this.cleanupPendingAnimations.bind(this)
    );
  }

  handleTurboStream(event) {
    const streamElement = event.target;
    const action = streamElement.getAttribute('action');

    // Only intercept remove actions
    if (action !== 'remove') return;

    const targetId = streamElement.getAttribute('target');
    if (!targetId) return;

    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    // Check if this element should be smoothly removed
    if (targetElement.dataset.smoothTurboRemove === 'true') {
      // Prevent the default removal
      event.preventDefault();

      this.smoothlyRemoveElement(targetElement);
    }
  }

  async smoothlyRemoveElement(element) {
    if (!element || !element.isConnected) return;

    const elementId = element.id;

    // Store a reference to this removal
    this.pendingRemovals.set(elementId, element);

    try {
      await hideElement(element);

      // After animation completes, actually remove the element from DOM
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    } catch (error) {
      console.error('Error during smooth removal animation:', error);

      // Fallback: remove immediately if animation fails
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    } finally {
      // Clean up our reference
      this.pendingRemovals.delete(elementId);
    }
  }

  cleanupPendingAnimations() {
    // If user navigates away while animations are in progress,
    // immediately remove any pending elements to prevent memory leaks
    for (const element of this.pendingRemovals.values()) {
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    }

    this.pendingRemovals.clear();
  }
}

const smoothTurboRemove = new SmoothTurboRemove();
